<template>
    <div class="widget-template-settings-form">
        <RedesignBaseForm
            ref="generalSettingsForm"
            :model="widgetSettingsModel"
            class="space-y-10"
        >
            <RedesignVerticalFormItem
                v-if="showTitleField"
                :title="String($t('widget.title'))"
                :description="String($t('widget.form.titleDescription'))"
                required
            >
                <RedesignFormItem
                    prop="TemplateName"
                    :rules="[required]"
                >
                    <RedesignBaseInput
                        v-model="widgetSettingsModel.TemplateName"
                    />
                </RedesignFormItem>
            </RedesignVerticalFormItem>
            <RedesignVerticalFormItem
                v-if="showTimeFrame && widgetTimeModel"
                :title="String($t('settings.time.frame'))"
            >
                <template #description>
                    <div class="text-sm font-normal break-keep">
                        {{ $t('widget.form.timeInfo.relative') }}
                        <br>
                        {{ $t('widget.form.timeInfo.absolute') }}
                        <br>
                        {{ $t('widget.form.timeInfo.period') }}
                    </div>
                </template>
                <div class="max-w-sm">
                    <RedesignWidgetTimeFrame
                        v-model="widgetTimeModel"
                        :time-frame-type="widgetTimeModel?.type"
                        :widget-time-options="widgetTimeOptions"
                    >
                        <template #frame-types>
                            <RedesignRadioGroup
                                v-model="widgetTimeModel.type"
                                class="radio-groups mb-4"
                            >
                                <RedesignRadioButton
                                    v-for="type in createWidgetTimeTypes"
                                    :key="type.value"
                                    :label="type.value"
                                >
                                    {{ type.label }}
                                </RedesignRadioButton>
                            </RedesignRadioGroup>
                        </template>
                    </RedesignWidgetTimeFrame>
                </div>
            </RedesignVerticalFormItem>
            <RedesignVerticalFormItem
                v-if="showStatisticsToDisplay"
                :title="String($t('statistics.to.display'))"
                :description="String($t('widget.form.statistic.Description'))"
            >
                <div class="max-w-sm">
                    <RedesignBaseSelect
                        v-model="showStatisticsModel"
                        :data="queueDashboardStatisticOptions"
                        :config="{
                            labelKey: 'label',
                            valueKey: 'key'
                        }"
                        full-width
                    />
                    <div class="flex w-full flex-col gap-3 pt-4">
                        <div class="flex">
                            <RedesignCheckbox v-model="sumOfOthersModel">
                                {{ $t('widget.displayPercentOfOthersValue') }}
                            </RedesignCheckbox>
                        </div>
                        <div class="flex">
                            <RedesignCheckbox v-model="absoluteNumbersModel">
                                {{ $t('widget.displayAbsoluteNumbers') }}
                            </RedesignCheckbox>
                        </div>
                    </div>
                </div>
            </RedesignVerticalFormItem>
            <RedesignVerticalFormItem
                v-if="showStatusSelect"
                :title="String($t('general.status'))"
                description=" "
            >
                <div class="max-w-sm">
                    <RedesignBaseSelect
                        v-model="statusModel"
                        :placeholder="String($t('common.selectStatus'))"
                        :multiple="false"
                        :data="statusList"
                        :config="{
                            labelKey: 'text',
                            valueKey: 'value'
                        }"
                        full-width
                    >
                        <template #option-item="{ option }">
                            <div
                                class="flex items-center gap-1.5"
                                :style="styleForStatusIconColor(option)"
                            >
                                <WidgetStatusIcon
                                    :icon="option?.icon"
                                    :color="'var(--status-svg-color)'"
                                    :size="16"
                                    class="widget-status-icon rounded"
                                />
                                <span class="w-16 mx-1">{{ $t(option.text) }}</span>
                            </div>
                        </template>
                        <template
                            v-if="selectedStatusData"
                            #prefix
                        >
                            <div
                                :style="styleForStatusIconColor(selectedStatusData)"
                                class="inline-flex items-center justify-center"
                            >
                                <WidgetStatusIcon
                                    :icon="selectedStatusData?.icon"
                                    :color="'var(--status-svg-color)'"
                                    :size="14"
                                    :offset="4"
                                    class="widget-status-icon rounded"
                                />
                            </div>
                        </template>
                    </RedesignBaseSelect>
                </div>
            </RedesignVerticalFormItem>

            <div v-if="wizardConfigs.length">
                <WidgetParameterSetupCard
                    ref="widgetParameterSetupCardRef"
                    :configs="wizardConfigs"
                    @update:config="updateFilterItem"
                />
            </div>
            <div v-if="requiredConfigs.length">
                <WidgetTemplatesSettingsList
                    ref="widgetTemplatesSettingsListRef"
                    :configs="requiredConfigs"
                    @update:config="updateFilterItem"
                />
            </div>
            <BaseCollapse
                v-if="optionalConfigs.length"
                v-model="activeCollapse"
            >
                <BaseCollapseItem
                    :title="String($t('widget.form.additionalSettings.label'))"
                    name="additionalSettings"
                >
                    <WidgetTemplatesSettingsList
                        :configs="optionalConfigs"
                        @update:config="updateFilterItem"
                    />
                </BaseCollapseItem>
            </BaseCollapse>
        </RedesignBaseForm>
    </div>
</template>

<script setup lang="ts">
import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
import {
    getWidgetStatusList,
    styleForStatusIconColor,
} from '@/components/RedesignComponents/Widgets/widget.helper'
import RedesignVerticalFormItem from '@/components/RedesignBaseComponents/Form/RedesignVerticalFormItem.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignWidgetTimeFrame from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetTimeFrame.vue'
import RedesignBaseForm from '@/components/RedesignBaseComponents/Form/RedesignBaseForm.vue'
import { IWidgetTemplateListItem } from '@/types/templates'
import { useVModel } from '@vueuse/core'
import { ComponentExposed } from 'vue-component-type-helpers'
import { computed, nextTick, ref } from 'vue'
import { ObjectAnyType, WidgetConfig } from '@/types/dashboard'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import useValidationRules from '@/composables/useValidationRules'
import WidgetStatusIcon from '@/components/RedesignComponents/Cards/WidgetStatusIcon.vue'
import BaseCollapseItem from '@/components/RedesignBaseComponents/Collapse/BaseCollapseItem.vue'
import BaseCollapse from '@/components/RedesignBaseComponents/Collapse/BaseCollapse.vue'
import WidgetTemplatesSettingsList
    from '@/components/RedesignComponents/Widgets/Create/WidgetTemplatesSettingsList.vue'
import WidgetParameterSetupCard
    from '@/components/RedesignComponents/Widgets/Parameters/WidgetParameterSetupCard.vue'
import {
    getWizardParameters,
    isWizardParameter
} from '@/components/RedesignComponents/Widgets/widgetParameterRegistry'
import {
    PERIOD_TYPE_PARAMETER_NAME,
    QUESTIONS_PARAMETER_NAME
} from '@/enum/widgetTemplateConfigs'
import { getAllQueueStatisticArray } from '@/enum/redesignQueueDashboardStatistics'
import i18n from '@/i18n'

const requiredParametersNames = [ PERIOD_TYPE_PARAMETER_NAME, QUESTIONS_PARAMETER_NAME ]

const { required } = useValidationRules()

const props = defineProps<{
    showTitleField?: boolean
    showStatusSelect?: boolean
    showStatisticsToDisplay?: boolean
    showTimeFrame?: boolean
    value: Partial<IWidgetTemplateListItem>
    uniqTemplatesConfigs: Array<WidgetConfig>
}>()

const emit = defineEmits<{
    (e: 'update:value', value: Partial<IWidgetTemplateListItem>): void
    (e: 'value', value: Partial<IWidgetTemplateListItem>): void
    (e: 'update:configs', configs: WidgetConfig): void
}>()

const widgetSettingsModel = useVModel(props, 'value', emit, { eventName: 'input' })

const generalSettingsForm = ref<typeof RedesignBaseForm>()
const widgetTemplatesSettingsListRef = ref<InstanceType<typeof WidgetTemplatesSettingsList>>()
const widgetParameterSetupCardRef = ref<ComponentExposed<typeof WidgetParameterSetupCard>>()
const activeCollapse = ref('')

const widgetTimeModel = computed({
    get () {
        return widgetSettingsModel.value.WidgetTime
    },
    set (val) {
        if (!widgetSettingsModel.value.WidgetTime) {
            widgetSettingsModel.value.WidgetTime = {
                Date_interval: '0',
                datedeff: '',
                type: 'relative'
            }
        }
        widgetSettingsModel.value.WidgetTime = val ? { ...val } : {
            Date_interval: '0',
            datedeff: '',
            type: 'relative'
        }
    }
})

const isRequired = (config: WidgetConfig) =>
    requiredParametersNames.includes(config.ParameterName ?? '')


const wizardConfigs = computed(() =>
    getWizardParameters(props.uniqTemplatesConfigs)
)

const inlineConfigs = computed(() =>
    props.uniqTemplatesConfigs.filter(config => !isWizardParameter(config))
)

const requiredConfigs = computed(() =>
    inlineConfigs.value.filter(isRequired)
)

const optionalConfigs = computed(() =>
    inlineConfigs.value.filter(config => !isRequired(config))
)

function ensureWidgetLayout () {
    if (!widgetSettingsModel.value.WidgetLayout) {
        widgetSettingsModel.value.WidgetLayout = {
            ShowStatistics: [],
            SumOfOthers: false,
            AbsoluteNumbers: false,
            status: undefined
        }
    }
}

const showStatisticsModel = computed({
    get () {
        return widgetSettingsModel.value.WidgetLayout?.ShowStatistics ?? []
    },
    set (val) {
        ensureWidgetLayout()
        if (widgetSettingsModel.value.WidgetLayout) {
            widgetSettingsModel.value.WidgetLayout.ShowStatistics = val
        }

    }
})

const sumOfOthersModel = computed({
    get () {
        return widgetSettingsModel.value.WidgetLayout?.SumOfOthers ?? false
    },
    set (val) {
        ensureWidgetLayout()
        if (widgetSettingsModel.value.WidgetLayout) {
            widgetSettingsModel.value.WidgetLayout.SumOfOthers = val
        }

    }
})

const absoluteNumbersModel = computed({
    get () {
        return widgetSettingsModel.value.WidgetLayout?.AbsoluteNumbers ?? false
    },
    set (val) {
        ensureWidgetLayout()
        if (widgetSettingsModel.value.WidgetLayout) {
            widgetSettingsModel.value.WidgetLayout.AbsoluteNumbers = val
        }
    }
})

const statusModel = computed({
    get () {
        return widgetSettingsModel.value.WidgetLayout?.status
    },
    set (val) {
        ensureWidgetLayout()
        if (widgetSettingsModel.value.WidgetLayout) {
            widgetSettingsModel.value.WidgetLayout.status = val
        }
    }
})

const queueDashboardStatisticOptions = computed(() => {
    const customConfig = widgetSettingsModel.value.WidgetLayout?.queueDashboardConfig ?? {}
    return getAllQueueStatisticArray().map(statistic => {
        return {
            key: statistic.key,
            label: customConfig[statistic.key]?.customLabel ? customConfig[statistic.key].customLabel : statistic.label
        }
    })
})

const createWidgetTimeTypes = computed(() => {
    return widgetTimeTypes
        .map(el => {
            return {
                label: i18n.t(el.text),
                value: el.label
            }
        })
})
const statusList = computed(() => {
    return getWidgetStatusList()
})
const selectedStatusData = computed(() => {
    if (!statusModel.value) {
        return undefined
    }

    return statusList.value.find((i: ObjectAnyType) => i.value === statusModel.value)
})

function updateFilterItem (config: WidgetConfig) {
    emit('update:configs', config)
}

async function validateForm () {
    const formValidation = await generalSettingsForm.value?.validate()

    let selectExtendedValid = true
    let wizardParametersValid = true

    if (widgetParameterSetupCardRef.value) {
        wizardParametersValid = widgetParameterSetupCardRef.value.validate()

        if (wizardParametersValid) {
            widgetParameterSetupCardRef.value.clearError()
        } else {
            widgetParameterSetupCardRef.value.showError()
        }
    }

    if (widgetTemplatesSettingsListRef.value) {
        selectExtendedValid = widgetTemplatesSettingsListRef.value.validateSelectExtended()

        await nextTick()

        if (!selectExtendedValid) {
            widgetTemplatesSettingsListRef.value.showSelectExtendedError()
        } else {
            widgetTemplatesSettingsListRef.value.clearSelectExtendedError()
        }
    }

    return {
        isValid: formValidation?.isValid && selectExtendedValid && wizardParametersValid,
        errors: formValidation?.errors || {}
    }
}

defineExpose({
    validate: validateForm
})
</script>
